import React, { useCallback, useEffect, useRef } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { isNil } from 'lodash'
import PropTypes from 'prop-types'

import { Select } from '../../../common/components/sections'
import { StyledEditabledSelectContainer } from '../../../common/components/styled'
import { HOTKEYS_ENABLED_TAGS } from '../../../common/constants'
import { optionPropType } from '../../../common/prop-types'

import { LOCATIONS_AVAILABLE_HOTKEYS } from '../../constants'
import {
  useLocationEditableCellHotkey,
  useUpdateLocationMutation,
} from '../../hooks'

const LocationsTableSelect = ({
  locationId,
  fieldName,
  placeholder,
  value,
  mode,
  options,
  isDisabled,
}) => {
  const selectRef = useRef(null)
  const { isSelectInHotkeyEditMode, setHotkeyEditModeEnabled } =
    useLocationEditableCellHotkey(locationId, fieldName)
  const [updateLocation] = useUpdateLocationMutation(locationId)

  useEffect(() => {
    if (isSelectInHotkeyEditMode && !isNil(selectRef.current)) {
      selectRef.current.focus()
    }
  }, [isSelectInHotkeyEditMode])

  const handleChange = useCallback(
    newValue => {
      updateLocation({
        [fieldName]: newValue,
      })
      selectRef.current.blur()
      setHotkeyEditModeEnabled(false)
    },
    [fieldName, setHotkeyEditModeEnabled, updateLocation],
  )

  const handleBlur = useCallback(() => {
    setHotkeyEditModeEnabled(false)
  }, [setHotkeyEditModeEnabled])

  useHotkeys(
    LOCATIONS_AVAILABLE_HOTKEYS.ESC.hotkey,
    () => {
      selectRef.current.blur()
    },
    {
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [isSelectInHotkeyEditMode],
  )

  return (
    <StyledEditabledSelectContainer>
      <Select
        ref={selectRef}
        showSearch
        mode={mode}
        options={options}
        placeholder={placeholder}
        value={value}
        allowClear
        onChange={handleChange}
        dropdownStyle={{ maxHeight: 400, minWidth: 250 }}
        onBlur={handleBlur}
        disabled={isDisabled}
      />
    </StyledEditabledSelectContainer>
  )
}

LocationsTableSelect.propTypes = {
  locationId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  value: PropTypes.string,
  mode: PropTypes.string,
  options: PropTypes.arrayOf(optionPropType),
}

LocationsTableSelect.defaultProps = {
  value: undefined,
  isDisabled: false,
  options: [],
  mode: undefined,
}

export default LocationsTableSelect
